import React, { useEffect, useState } from 'react';
import { useParams, Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { publicAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  ShoppingBag, BookOpen, MessageCircle, ArrowRight, 
  Check, Star, Phone, Mail, MapPin, Sparkles, Layers
} from 'lucide-react';
import PublicAIInput from '../../components/chat/PublicAIInput';
import TenantBlog from './TenantBlog';
import TenantArticle from './TenantArticle';

const BACKEND_URL = '';

// Tenant Portal Landing Page
function TenantLanding({ tenant, services, kbArticles }) {
  const navigate = useNavigate();
  
  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 animate-pulse" />
          <p className="text-gray-500">Loading portal...</p>
        </div>
      </div>
    );
  }

  const logoUrl = tenant.logo_url?.startsWith('http') 
    ? tenant.logo_url 
    : tenant.logo_url ? `${BACKEND_URL}${tenant.logo_url}` : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={tenant.name} className="h-10 w-auto" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">{tenant.name?.charAt(0)}</span>
              </div>
            )}
            <span className="font-semibold text-xl text-gray-900">{tenant.name}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="services" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Services
            </Link>
            <Link 
              to="knowledge-base" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Help Center
            </Link>
            <Link 
              to="blog" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Blog
            </Link>
            <Link 
              to="login" 
              className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
            >
              Client Login
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {tenant.hero_headline || `Welcome to ${tenant.name}`}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {tenant.hero_subheadline || 'Professional services tailored to your needs. Browse our offerings and get started today.'}
          </p>
          
          {/* AI Chat Input */}
          <div className="max-w-2xl mx-auto mb-8">
            <PublicAIInput tenantId={tenant.id} />
          </div>
          
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => navigate('services')}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-medium rounded-lg hover:shadow-lg transition-all"
            >
              Browse Services
            </button>
            <button
              onClick={() => navigate('contact')}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      {services && services.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
              <p className="text-gray-600">Explore our range of professional services</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.slice(0, 6).map((service) => (
                <Link
                  key={service.id}
                  to={`services/${service.id}`}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:border-orange-200 transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center mb-4">
                    <ShoppingBag className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {service.short_description || service.description?.slice(0, 100)}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-900">
                      ${service.pricing_tiers?.[0]?.price || service.price || '0'}
                      <span className="text-sm font-normal text-gray-500">
                        /{service.billing_cycle || 'mo'}
                      </span>
                    </span>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
            
            {services.length > 6 && (
              <div className="text-center mt-8">
                <Link
                  to="services"
                  className="inline-flex items-center gap-2 text-orange-500 font-medium hover:text-orange-600"
                >
                  View all services
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Knowledge Base Preview */}
      {kbArticles && kbArticles.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Help Center</h2>
              <p className="text-gray-600">Find answers to common questions</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {kbArticles.slice(0, 4).map((article) => (
                <Link
                  key={article.id}
                  to={`knowledge-base/${article.id}`}
                  className="p-4 rounded-lg border border-gray-100 hover:border-orange-200 hover:bg-orange-50/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900 group-hover:text-orange-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">{article.category}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={tenant.name} className="h-8 w-auto" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{tenant.name?.charAt(0)}</span>
                </div>
              )}
              <span className="font-medium text-gray-900">{tenant.name}</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-gray-600">
              {tenant.contact_email && (
                <a href={`mailto:${tenant.contact_email}`} className="flex items-center gap-2 hover:text-gray-900">
                  <Mail className="w-4 h-4" />
                  {tenant.contact_email}
                </a>
              )}
              {tenant.contact_phone && (
                <a href={`tel:${tenant.contact_phone}`} className="flex items-center gap-2 hover:text-gray-900">
                  <Phone className="w-4 h-4" />
                  {tenant.contact_phone}
                </a>
              )}
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-100 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} {tenant.name}. All rights reserved.
            <span className="mx-2">•</span>
            Powered by <a href="https://edrano.my" className="text-orange-500 hover:underline">Edrano</a>
            <span className="mx-2">•</span>
            Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Themelio</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Portal Header Component (shared across sub-pages)
function PortalHeader({ tenant }) {
  const BACKEND_URL = '';
  const logoUrl = tenant?.logo_url?.startsWith('http') 
    ? tenant.logo_url 
    : tenant?.logo_url ? `${BACKEND_URL}${tenant.logo_url}` : null;
  
  const basePath = `/app/${tenant?.slug || 'demo'}`;

  return (
    <header className="border-b border-gray-100 bg-white sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to={basePath} className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={tenant?.name} className="h-10 w-auto" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">{tenant?.name?.charAt(0)}</span>
            </div>
          )}
          <span className="font-semibold text-xl text-gray-900">{tenant?.name}</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to={`${basePath}/services`} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
            Services
          </Link>
          <Link to={`${basePath}/knowledge-base`} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
            Help Center
          </Link>
          <Link to={`${basePath}/blog`} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
            Blog
          </Link>
          <Link 
            to={`${basePath}/login`} 
            className="text-sm px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          >
            Client Login
          </Link>
        </div>
      </div>
    </header>
  );
}

// Portal Footer Component (shared across sub-pages)
function PortalFooter({ tenant }) {
  const BACKEND_URL = '';
  const logoUrl = tenant?.logo_url?.startsWith('http') 
    ? tenant.logo_url 
    : tenant?.logo_url ? `${BACKEND_URL}${tenant.logo_url}` : null;

  return (
    <footer className="border-t border-gray-100 py-12 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={tenant?.name} className="h-8 w-auto" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">{tenant?.name?.charAt(0)}</span>
              </div>
            )}
            <span className="font-medium text-gray-900">{tenant?.name}</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-600">
            {tenant?.contact_email && (
              <a href={`mailto:${tenant.contact_email}`} className="flex items-center gap-2 hover:text-gray-900">
                <Mail className="w-4 h-4" />
                {tenant.contact_email}
              </a>
            )}
            {tenant?.contact_phone && (
              <a href={`tel:${tenant.contact_phone}`} className="flex items-center gap-2 hover:text-gray-900">
                <Phone className="w-4 h-4" />
                {tenant.contact_phone}
              </a>
            )}
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-100 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} {tenant?.name}. All rights reserved.
          <span className="mx-2">•</span>
          Powered by <a href="https://edrano.my" className="text-orange-500 hover:underline">Edrano</a>
          <span className="mx-2">•</span>
          Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Themelio</a>
        </div>
      </div>
    </footer>
  );
}

// Services List Page
function TenantServicesPage({ tenant, services }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our range of professional services designed to help your business succeed.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services?.map((service) => (
            <Link 
              key={service.id}
              to={service.id}
              className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${tenant?.primary_color || '#f97316'}20` }}
                  >
                    <ShoppingBag className="w-6 h-6" style={{ color: tenant?.primary_color || '#f97316' }} />
                  </div>
                  {service.is_popular && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      Popular
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {service.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-gray-900">
                    ${service.price}
                    <span className="text-sm font-normal text-gray-500">
                      /{service.billing_cycle === 'monthly' ? 'mo' : service.billing_cycle === 'yearly' ? 'yr' : 'once'}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        {(!services || services.length === 0) && (
          <div className="text-center py-12">
            <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Available</h3>
            <p className="text-gray-500">Check back soon for our service offerings.</p>
          </div>
        )}
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Service Detail Page
function TenantServiceDetail({ tenant, services }) {
  const { serviceId } = useParams();
  const service = services?.find(s => s.id === serviceId);
  const basePath = `/app/${tenant?.slug || 'demo'}`;
  
  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PortalHeader tenant={tenant} />
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Service Not Found</h1>
          <p className="text-gray-600 mb-6">The service you're looking for doesn't exist.</p>
          <Link 
            to={`${basePath}/services`} 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Browse All Services
          </Link>
        </main>
        <PortalFooter tenant={tenant} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <Link to={`${basePath}/services`} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-8">
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Services
        </Link>
        
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{service.name}</h1>
                <p className="text-gray-600">{service.description}</p>
              </div>
              {service.is_popular && (
                <span className="px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                  Popular Choice
                </span>
              )}
            </div>
            
            <div className="flex items-baseline gap-2 mb-8">
              <span className="text-4xl font-bold text-gray-900">${service.price}</span>
              <span className="text-gray-500">
                /{service.billing_cycle === 'monthly' ? 'month' : service.billing_cycle === 'yearly' ? 'year' : 'one-time'}
              </span>
            </div>
            
            {service.features && service.features.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex gap-4">
              <Link 
                to={`${basePath}/login`}
                className="flex-1 text-center px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Sign Up to Purchase
              </Link>
              <Link 
                to={basePath}
                className="px-6 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Knowledge Base Page
function TenantKnowledgeBase({ tenant, kbArticles }) {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredArticles = kbArticles?.filter(article => 
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Group by category
  const articlesByCategory = filteredArticles?.reduce((acc, article) => {
    const cat = article.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(article);
    return acc;
  }, {});
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-lg text-gray-600 mb-8">
            Find answers to common questions and learn how to get the most out of our services.
          </p>
          
          <div className="max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
        
        {articlesByCategory && Object.keys(articlesByCategory).length > 0 ? (
          <div className="space-y-8">
            {Object.entries(articlesByCategory).map(([category, articles]) => (
              <div key={category}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{category}</h2>
                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {articles.map((article) => (
                    <Link 
                      key={article.id}
                      to={article.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-900">{article.title}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Articles Found</h3>
            <p className="text-gray-500">
              {searchQuery ? 'Try a different search term.' : 'Check back soon for helpful articles.'}
            </p>
          </div>
        )}
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// KB Article Detail Page
function TenantKBArticle({ tenant, kbArticles }) {
  const { articleId } = useParams();
  const article = kbArticles?.find(a => a.id === articleId);
  const basePath = `/app/${tenant?.slug || 'demo'}`;
  
  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PortalHeader tenant={tenant} />
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link 
            to={`${basePath}/knowledge-base`} 
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Browse Help Center
          </Link>
        </main>
        <PortalFooter tenant={tenant} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <Link to={`${basePath}/knowledge-base`} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-8">
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Help Center
        </Link>
        
        <article className="bg-white rounded-2xl border border-gray-200 p-8">
          <div className="mb-6">
            <span className="text-sm text-orange-600 font-medium">{article.category}</span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{article.title}</h1>
          </div>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {article.content || 'This article is coming soon.'}
            </p>
          </div>
        </article>
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Client Login Page
function TenantClientLogin({ tenant }) {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const basePath = `/app/${tenant?.slug || 'demo'}`;
  const BACKEND_URL = '';
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // For demo, show a message that this is a demo
    if (tenant?.is_demo) {
      toast.info('This is a demo portal. Client accounts are not available in demo mode.');
      setLoading(false);
      return;
    }
    
    try {
      const endpoint = mode === 'login' 
        ? `${BACKEND_URL}/api/client-portal/login`
        : `${BACKEND_URL}/api/client-portal/register`;
      
      const body = mode === 'login'
        ? { email, password, tenant_slug: tenant?.slug }
        : { email, password, full_name: fullName, phone, tenant_slug: tenant?.slug };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch (parseErr) {
        throw new Error('Server error. Please try again later.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }
      
      // Store token and user data
      localStorage.setItem('clientToken', data.access_token);
      localStorage.setItem('clientUser', JSON.stringify(data.user));
      
      toast.success(mode === 'login' ? 'Welcome back!' : 'Registration successful!');
      navigate(`${basePath}/dashboard`);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {mode === 'login' ? 'Client Login' : 'Create Account'}
            </h1>
            <p className="text-gray-600">
              {mode === 'login' 
                ? 'Sign in to access your dashboard and manage your services.'
                : 'Create an account to get started with our services.'}
            </p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-600">
            {mode === 'login' ? (
              <>
                Don't have an account?{' '}
                <button 
                  onClick={() => setMode('register')} 
                  className="text-orange-600 hover:underline"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button 
                  onClick={() => setMode('login')} 
                  className="text-orange-600 hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Client Dashboard Page
function TenantClientDashboard({ tenant }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const basePath = `/app/${tenant?.slug || 'demo'}`;
  const BACKEND_URL = '';
  
  useEffect(() => {
    const fetchDashboard = async () => {
      const token = localStorage.getItem('clientToken');
      if (!token) {
        navigate(`${basePath}/login`);
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/client-portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.status === 401) {
          localStorage.removeItem('clientToken');
          localStorage.removeItem('clientUser');
          navigate(`${basePath}/login`);
          return;
        }
        
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboard();
  }, [BACKEND_URL, basePath, navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientUser');
    toast.success('Logged out successfully');
    navigate(`${basePath}/login`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Client Dashboard Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={basePath} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">{tenant?.name?.charAt(0)}</span>
            </div>
            <span className="font-semibold text-xl text-gray-900">{tenant?.name}</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, {dashboardData?.user?.full_name}
            </span>
            <button 
              onClick={handleLogout}
              className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">My Dashboard</h1>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {dashboardData?.stats?.active_services || 0}
            </div>
            <div className="text-sm text-gray-500">Active Services</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              ${dashboardData?.stats?.total_spent?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-gray-500">Total Spent</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {dashboardData?.stats?.upcoming_renewals || 0}
            </div>
            <div className="text-sm text-gray-500">Upcoming Renewals</div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link 
            to={`${basePath}/services`}
            className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Browse Services</div>
              <div className="text-sm text-gray-500">View available services</div>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>
          
          <Link 
            to={`${basePath}/my-services`}
            className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <Layers className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">My Services</div>
              <div className="text-sm text-gray-500">Manage your active services</div>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto" />
          </Link>
        </div>
        
        {/* Recent Services */}
        {dashboardData?.services && dashboardData.services.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">My Services</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboardData.services.map((service, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{service.service_name || service.name}</div>
                    <div className="text-sm text-gray-500">
                      Status: <span className={service.status === 'active' ? 'text-green-600' : 'text-gray-500'}>
                        {service.status}
                      </span>
                    </div>
                  </div>
                  {service.expires_at && (
                    <div className="text-sm text-gray-500">
                      Expires: {new Date(service.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 mb-2">No Active Services</h3>
            <p className="text-gray-500 mb-4">You haven't purchased any services yet.</p>
            <Link 
              to={`${basePath}/services`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Browse Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Client My Services Page
function TenantMyServices({ tenant }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const basePath = `/app/${tenant?.slug || 'demo'}`;
  const BACKEND_URL = '';
  
  useEffect(() => {
    const fetchServices = async () => {
      const token = localStorage.getItem('clientToken');
      if (!token) {
        navigate(`${basePath}/login`);
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/client-portal/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.status === 401) {
          navigate(`${basePath}/login`);
          return;
        }
        
        const data = await response.json();
        setServices(data.services || []);
      } catch (err) {
        console.error('Failed to fetch services:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServices();
  }, [BACKEND_URL, basePath, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <PortalHeader tenant={tenant} />
      
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to={`${basePath}/dashboard`} className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
              ← Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">My Services</h1>
          </div>
        </div>
        
        {services.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">
                      {service.service_name || service.name}
                    </h3>
                    <p className="text-sm text-gray-500">{service.service_description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    service.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {service.status}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  {service.purchased_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Purchased:</span>
                      <span className="text-gray-900">{new Date(service.purchased_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {service.expires_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expires:</span>
                      <span className="text-gray-900">{new Date(service.expires_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 mb-2">No Services Yet</h3>
            <p className="text-gray-500 mb-4">You haven't purchased any services.</p>
            <Link 
              to={`${basePath}/services`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Browse Services
            </Link>
          </div>
        )}
      </main>
      
      <PortalFooter tenant={tenant} />
    </div>
  );
}

// Main Tenant Portal Component
export default function TenantPortal() {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const basePath = `/app/${tenantSlug || 'demo'}`;

  useEffect(() => {
    loadTenantData();
  }, [tenantSlug]);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch tenant data by slug
      const res = await publicAPI.get(`/portal/${tenantSlug}`);
      
      if (res.data) {
        setTenant(res.data.tenant);
        setServices(res.data.services || []);
        setKbArticles(res.data.kb_articles || []);
      }
    } catch (err) {
      console.error('Failed to load tenant data:', err);
      if (err.response?.status === 404) {
        setError('Portal not found');
      } else {
        setError('Failed to load portal');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-2xl">🔍</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Portal Not Found</h1>
          <p className="text-gray-600 mb-6">
            The portal "{tenantSlug}" doesn't exist or is not available.
          </p>
          <a 
            href="https://edrano.my" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Go to Edrano
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route index element={<TenantLanding tenant={tenant} services={services} kbArticles={kbArticles} />} />
      <Route path="services" element={<TenantServicesPage tenant={tenant} services={services} />} />
      <Route path="services/:serviceId" element={<TenantServiceDetail tenant={tenant} services={services} />} />
      <Route path="knowledge-base" element={<TenantKnowledgeBase tenant={tenant} kbArticles={kbArticles} />} />
      <Route path="knowledge-base/:articleId" element={<TenantKBArticle tenant={tenant} kbArticles={kbArticles} />} />
      <Route path="login" element={<TenantClientLogin tenant={tenant} />} />
      <Route path="blog" element={<TenantBlog tenant={tenant} basePath={basePath} />} />
      <Route path="blog/:articleSlug" element={<TenantArticle tenant={tenant} basePath={basePath} />} />
      <Route path="dashboard" element={<TenantClientDashboard tenant={tenant} />} />
      <Route path="my-services" element={<TenantMyServices tenant={tenant} />} />
      <Route path="*" element={<TenantLanding tenant={tenant} services={services} kbArticles={kbArticles} />} />
    </Routes>
  );
}
